// src/plugins/sales-forecast/lib/useSigmaElementData.ts
import { useConfig, useElementData, useElementColumns } from "@sigmacomputing/plugin";

/** The typed rows your app/API expects */
export type EhRow = {
  address: string | null;
  city: string | null;
  neighborhood: string | null;
  neighborhoodGroup: string | null;
  houseType: string | null;
  propertyType: string | null;
  contractDate: string | null; // ISO string
  officeGreenLight: string | null;
  activeComps: number | null;
  market: string | null;
  schoolDistrict: string | null;
  floorPlan: string | null;
  style: string | null;
  price: number | null;
  sqft: number | null;
};

/** Convenience aliases */
type ColId = string;
type DisplayName = string;
type ColumnMeta = { name?: string };
type RawCols = Record<ColId, ColumnMeta>;
type ColumnArrays = Record<ColId, any[]>;

/* ------------------------- helpers ------------------------- */

function norm(s?: string) {
  return (s ?? "").toLowerCase().replace(/\s+/g, " ").trim();
}
function softNorm(s?: string) {
  return (s ?? "").toLowerCase().replace(/[\s_\-./]+/g, "").trim();
}

/** Find a column id in rawCols by (case-insensitive) display name */
function findColIdByDisplayName(rawCols: RawCols, ...candidates: DisplayName[]): ColId | null {
  const wanted = candidates.map(norm);
  for (const [id, meta] of Object.entries(rawCols)) {
    if (wanted.includes(norm(meta?.name))) return id;
  }
  return null;
}

/** Translate a column id to its human display name */
function idToName(rawCols: RawCols, id: ColId | null): DisplayName {
  return id ? rawCols[id]?.name ?? "(unknown)" : "(unresolved)";
}

/** Convert whatever shape `useElementData` returns into { colId: value[] } */
function asColumnArrays(data: any): ColumnArrays | null {
  if (!data) return null;

  // Common Sigma shape: object of arrays keyed by columnId
  if (typeof data === "object" && !Array.isArray(data)) {
    const keys = Object.keys(data);
    if (keys.every((k) => Array.isArray((data as any)[k]))) {
      return data as ColumnArrays;
    }
  }

  // Defensive: array of row objects (we'll transpose)
  if (Array.isArray(data) && data.length && typeof data[0] === "object") {
    const cols: ColumnArrays = {};
    for (const row of data as any[]) {
      for (const [k, v] of Object.entries(row)) {
        (cols[k] ??= []).push(v);
      }
    }
    return cols;
  }

  return null;
}

/**
 * Resolve the *data* column ID actually present in the element's output for a desired field.
 */
function resolveDataIdForField(opts: {
  intendedId: ColId | null;
  intendedName: DisplayName; // from rawCols
  presentDataIds: ColId[];
  rawCols: RawCols;
}): { dataId: ColId | null; via: "id" | "name" | "soft-name" | "none" } {
  const { intendedId, intendedName, presentDataIds, rawCols } = opts;

  // 1) Exact id hit
  if (intendedId && presentDataIds.includes(intendedId)) {
    return { dataId: intendedId, via: "id" };
  }

  // Build name maps once
  const nameToDataIdExact: Record<string, ColId> = {};
  const softNameToDataId: Record<string, ColId> = {};
  for (const id of presentDataIds) {
    const nm = rawCols[id]?.name;
    if (!nm) continue;
    const n = norm(nm);
    const sn = softNorm(nm);
    if (!(n in nameToDataIdExact)) nameToDataIdExact[n] = id;
    if (!(sn in softNameToDataId)) softNameToDataId[sn] = id;
  }

  // 2) Exact name
  const intendedNorm = norm(intendedName);
  if (intendedNorm && nameToDataIdExact[intendedNorm]) {
    return { dataId: nameToDataIdExact[intendedNorm], via: "name" };
  }

  // 3) Soft name
  const intendedSoft = softNorm(intendedName);
  if (intendedSoft && softNameToDataId[intendedSoft]) {
    return { dataId: softNameToDataId[intendedSoft], via: "soft-name" };
  }

  return { dataId: null, via: "none" };
}

/* --------------------------- hook -------------------------- */

export function useSigmaElementData(): EhRow[] {
  const cfg = useConfig() as any;

  // Prefer editor-panel key "ehMaster"; fall back to legacy "dataSource"
  const ehMasterMaybe: string | undefined = (cfg?.ehMaster ?? cfg?.dataSource) || undefined;

  // Hooks must be called with string → coerce undefined → ""
  const elementId: string = ehMasterMaybe ?? "";

  const rawDataAny = useElementData(elementId);
  const rawCols = useElementColumns(elementId);

  // Not bound or not streamed yet
  if (!ehMasterMaybe || !rawDataAny || !rawCols) return [];

  // Normalize data to object-of-arrays
  const colArrays = asColumnArrays(rawDataAny);
  if (!colArrays) return [];

  /* ----------------- 1) intended IDs (config/fallback) ----------------- */
  /**
   * IMPORTANT:
   * - neighborhoodCol MUST map to "Neighborhood"
   * - neighborhoodGroupCol MUST map to "Neighborhood - Grouped"
   */
  const intendedIds: Record<keyof EhRow, ColId | null> = {
    address: cfg?.addressCol ?? findColIdByDisplayName(rawCols, "Address"),
    city: cfg?.cityCol ?? findColIdByDisplayName(rawCols, "City"),
    neighborhood: cfg?.neighborhoodCol ?? findColIdByDisplayName(rawCols, "Neighborhood"),
    neighborhoodGroup: cfg?.neighborhoodGroupCol ?? findColIdByDisplayName( rawCols, "Neighborhood - Grouped", "Neighborhood Grouped"),
    houseType: cfg?.houseTypeCol ?? findColIdByDisplayName(rawCols, "House Type"),
    propertyType: cfg?.propertyTypeCol ?? findColIdByDisplayName(rawCols, "Property Type", "PropertyType"),
    contractDate: cfg?.contractDateCol ?? findColIdByDisplayName(rawCols, "Contract Date"),
    officeGreenLight: cfg?.officeGreenLightCol ?? findColIdByDisplayName(rawCols, "Office Green Light"),
    activeComps: cfg?.activeCompsCol ?? findColIdByDisplayName(rawCols, "Active Comps"),
    market: cfg?.marketCol ?? findColIdByDisplayName(rawCols, "Market"),
    schoolDistrict: cfg?.schoolDistrictCol ?? findColIdByDisplayName(rawCols, "School District"),
    floorPlan: cfg?.floorPlanCol ?? findColIdByDisplayName(rawCols, "Floor Plan"),
    style: cfg?.styleCol ?? findColIdByDisplayName(rawCols, "Style"),
    price: cfg?.priceCol ?? findColIdByDisplayName(rawCols, "Price"),
    sqft: cfg?.sqftCol ?? findColIdByDisplayName(rawCols, "Square Feet"),
  };

  /* ----------------- 2) intended display names ----------------- */

  const intendedNames: Record<keyof EhRow, DisplayName> = {
    address: idToName(rawCols, intendedIds.address),
    city: idToName(rawCols, intendedIds.city),
    neighborhood: idToName(rawCols, intendedIds.neighborhood),
    neighborhoodGroup: idToName(rawCols, intendedIds.neighborhoodGroup),
    houseType: idToName(rawCols, intendedIds.houseType),
    propertyType: idToName(rawCols, intendedIds.propertyType),
    contractDate: idToName(rawCols, intendedIds.contractDate),
    officeGreenLight: idToName(rawCols, intendedIds.officeGreenLight),
    activeComps: idToName(rawCols, intendedIds.activeComps),
    market: idToName(rawCols, intendedIds.market),
    schoolDistrict: idToName(rawCols, intendedIds.schoolDistrict),
    floorPlan: idToName(rawCols, intendedIds.floorPlan),
    style: idToName(rawCols, intendedIds.style),
    price: idToName(rawCols, intendedIds.price),
    sqft: idToName(rawCols, intendedIds.sqft),
  };

  // Present data IDs are the keys of the column arrays
  const presentDataIds = Object.keys(colArrays);

  /* -------- 3) Resolve each field → actual data ID currently present -------- */

  const resolved: Record<
    keyof EhRow,
    { dataId: ColId | null; via: "id" | "name" | "soft-name" | "none" }
  > = {} as any;

  (Object.keys(intendedIds) as (keyof EhRow)[]).forEach((field) => {
    resolved[field] = resolveDataIdForField({
      intendedId: intendedIds[field],
      intendedName: intendedNames[field],
      presentDataIds,
      rawCols,
    });
  });

  const dataIdsForFields: Record<keyof EhRow, ColId | null> = {} as any;
  (Object.keys(resolved) as (keyof EhRow)[]).forEach((field) => {
    dataIdsForFields[field] = resolved[field].dataId;
  });

  /* ------------- 4) Determine row count & defensive guards ------------- */

  let idForLength: ColId | null =
    dataIdsForFields.address ??
    dataIdsForFields.city ??
    dataIdsForFields.neighborhood ??
    dataIdsForFields.neighborhoodGroup ??
    dataIdsForFields.houseType ??
    dataIdsForFields.propertyType ??
    dataIdsForFields.contractDate ??
    dataIdsForFields.officeGreenLight ??
    dataIdsForFields.activeComps ??
    dataIdsForFields.market ??
    dataIdsForFields.schoolDistrict ??
    dataIdsForFields.floorPlan ??
    dataIdsForFields.style ??
    dataIdsForFields.price ??
    dataIdsForFields.sqft ??
    null;

  if (!idForLength) idForLength = presentDataIds[0] ?? null;
  if (!idForLength) return [];

  const sourceArray = colArrays[idForLength];
  if (!Array.isArray(sourceArray) || sourceArray.length === 0) return [];

  const len = sourceArray.length;

  const pick = (dataId: ColId | null, i: number) =>
    dataId && Array.isArray(colArrays[dataId]) ? colArrays[dataId][i] : null;

  /* --------------------------- 5) Build typed rows -------------------------- */

  const rows: EhRow[] = new Array(len);
  for (let i = 0; i < len; i++) {
    const dtContract = pick(dataIdsForFields.contractDate, i);
    const dtOffice = pick(dataIdsForFields.officeGreenLight, i);

    const numVal = (field: keyof EhRow) => {
      const v = pick(dataIdsForFields[field], i);
      return v !== null && v !== undefined ? Number(v) : null;
    };

    const strVal = (field: keyof EhRow) => {
      const v = pick(dataIdsForFields[field], i);
      return v != null ? String(v) : null;
    };

    rows[i] = {
      address: strVal("address"),
      city: strVal("city"),
      neighborhood: strVal("neighborhood"),
      neighborhoodGroup: strVal("neighborhoodGroup"),
      houseType: strVal("houseType"),
      propertyType: strVal("propertyType"),
      contractDate: dtContract ? new Date(dtContract).toISOString() : null,
      officeGreenLight: dtOffice ? new Date(dtOffice).toISOString() : null,
      activeComps: numVal("activeComps"),
      market: strVal("market"),
      schoolDistrict: strVal("schoolDistrict"),
      floorPlan: strVal("floorPlan"),
      style: strVal("style"),
      price: numVal("price"),
      sqft: numVal("sqft"),
    };
  }

  return rows;
}